import {
  useGetBlobsQuery,
  useGetBuildingPackageFilesQuery,
  useGetLayoutOptimizationFilesQuery,
} from '@consigli/hooks';
import { Blob, File } from '@consigli/types';

import { ServiceType } from '@/utils/types';

interface UseGetDocumentsProps {
  projectId: number;
  serviceId: string;
  serviceType: ServiceType;
  page?: number;
  pageSize?: number;
}

export function useGetDocuments({
  projectId,
  serviceId,
  serviceType,
  page,
  pageSize,
}: UseGetDocumentsProps) {
  const usePagination = serviceType === ServiceType.DOCUMENT_PACKAGE && page !== undefined;

  const documentPackageQuery = useGetBlobsQuery(
    {
      projectId,
      packageId: serviceId,
      page: usePagination ? page : 'all',
      ...(usePagination && pageSize ? { pageSize } : {}),
    },
    { skip: serviceType !== ServiceType.DOCUMENT_PACKAGE },
  );

  const layoutFilesQuery = useGetLayoutOptimizationFilesQuery(
    {
      projectId,
      layoutId: serviceId,
      page: 'all',
    },
    { skip: serviceType !== ServiceType.LAYOUT_OPTIMIZATION },
  );

  const buildingPackageQuery = useGetBuildingPackageFilesQuery(
    {
      projectId,
      buildingPackageId: serviceId,
      page: 'all',
    },
    { skip: serviceType !== ServiceType.BUILDING_PACKAGE },
  );

  let data: (Blob | File)[] = [];
  let isLoading = false;
  let isFetching = false;
  let totalCount = 0;

  if (serviceType === ServiceType.DOCUMENT_PACKAGE) {
    data = documentPackageQuery.data?.results || [];
    isLoading = documentPackageQuery.isLoading;
    isFetching = documentPackageQuery.isFetching;
    totalCount = documentPackageQuery.data?.count || 0;
  } else if (serviceType === ServiceType.LAYOUT_OPTIMIZATION) {
    data = layoutFilesQuery.data?.results || [];
    isLoading = layoutFilesQuery.isLoading;
    isFetching = layoutFilesQuery.isFetching;
    totalCount = data.length;
  } else if (serviceType === ServiceType.BUILDING_PACKAGE) {
    data = buildingPackageQuery.data?.results || [];
    isLoading = buildingPackageQuery.isLoading;
    isFetching = buildingPackageQuery.isFetching;
    totalCount = data.length;
  }

  return { data, isLoading, isFetching, totalCount };
}
